import type { UsuarioItem, UsuariosFields } from '@/shared/api/controllers/usuarios';
import type { EditUsuarioValues } from './schema';
import {
  PERFILES,
  paisCodeToLabel,
  paisLabelToCode,
  type PaisLabel,
  type Perfil,
} from '../../constants';

function asPerfil(v: string | null | undefined): Perfil {
  return (PERFILES as readonly string[]).includes(v ?? '') ? (v as Perfil) : 'Visitante';
}

/** Reconstruct the form's `paises` array from the stored fields. Prefers
 *  `Paises_Permitidos_USR` (the array of ISO codes); falls back to the
 *  single `Pais_USR` label so legacy rows still load with at least one
 *  country selected. The default landing country comes first so the
 *  Select shows it as the primary. */
function readPaises(f: UsuariosFields): PaisLabel[] {
  const primaryLabel = (f.Pais_USR === 'Argentina' || f.Pais_USR === 'Mexico')
    ? (f.Pais_USR as PaisLabel)
    : null;
  const permittedLabels = (f.Paises_Permitidos_USR ?? [])
    .map((c) => paisCodeToLabel(c))
    .filter((l): l is PaisLabel => l !== null);
  if (permittedLabels.length === 0) {
    return primaryLabel ? [primaryLabel] : [];
  }
  const ordered: PaisLabel[] = [];
  if (primaryLabel && permittedLabels.includes(primaryLabel)) ordered.push(primaryLabel);
  for (const l of permittedLabels) if (!ordered.includes(l)) ordered.push(l);
  return ordered;
}

export function itemToEditForm(item: UsuarioItem): EditUsuarioValues {
  const f = item.fields;
  const paises = readPaises(f);
  return {
    nombre: f.Nombre_USR ?? '',
    usuarioApp: f.UsuarioApp_USR ?? '',
    perfil: asPerfil(f.Perfil_USR),
    paises,
    password: '', // intentionally blank — empty means "keep the existing password"
  };
}

function buildCountryFields(paises: PaisLabel[]): Partial<UsuariosFields> {
  const primary = paises[0] ?? null;
  const codes = paises
    .map((l) => paisLabelToCode(l))
    .filter((c): c is 'AR' | 'MX' => c !== null);
  return {
    Pais_USR: primary,
    Pais_Codigo_USR: primary ? paisLabelToCode(primary) : null,
    Paises_Permitidos_USR: codes,
  };
}

export function editFormToFields(values: EditUsuarioValues): Partial<UsuariosFields> {
  const fields: Partial<UsuariosFields> = {
    Nombre_USR: values.nombre.trim(),
    UsuarioApp_USR: values.usuarioApp.trim(),
    Perfil_USR: values.perfil,
    ...buildCountryFields(values.paises),
  };
  // Only patch the password when the admin actually typed one.
  if (values.password && values.password.length > 0) {
    fields.Password_USR = values.password;
  }
  return fields;
}

/** Create form shares the same mapping but password is always present. */
export function createFormToFields(values: EditUsuarioValues): Partial<UsuariosFields> {
  return {
    Nombre_USR: values.nombre.trim(),
    UsuarioApp_USR: values.usuarioApp.trim(),
    Perfil_USR: values.perfil,
    ...buildCountryFields(values.paises),
    Password_USR: values.password ?? '',
  };
}
